import { useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { Button } from '@domcrm/embed-ui';

import { postSelectClientOrganization } from '../widget-api/auth-me';
import { ApiError } from '../widget-api/http-client';
import { useEmbedBootstrap } from '../use-widget-bootstrap';
import { useQuerySuffix } from '../widget-hooks/useQuerySuffix';
import { useWidgetSeo } from '../widget-hooks/use-widget-seo';
import { clearEmbedSession, setEmbedSession } from '../widget-lib/embed-session-store';
import { PATH } from '../widget-lib/path-routes';

function searchWithOrgSlug(searchParams: URLSearchParams, newSlug: string): string {
  const p = new URLSearchParams(searchParams);
  p.set('org_slug', newSlug);
  p.delete('org');
  const s = p.toString();
  return s ? `?${s}` : '';
}

/**
 * Список членств пользователя в организациях.
 * В глобальном режиме — пост-логин пикер с поиском.
 * Переключение: POST /auth/me/select-client-organization (ADR-0009).
 */
export default function OrganizationsPage() {
  useWidgetSeo(
    'Выбор организации',
    'Список ваших организаций и профилей в системе.'
  );
  const suffix = useQuerySuffix();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const embedGlobal = searchParams.get('embed_global') === '1';
  const { accessToken, me, meLoading, meError, reloadMe, brandEmbedAllowed, orgSlug } =
    useEmbedBootstrap();

  const [query, setQuery] = useState('');
  const [switchError, setSwitchError] = useState<string | null>(null);
  const [switchingOrgId, setSwitchingOrgId] = useState<number | null>(null);

  const rows = useMemo(
    () => (me?.memberships ?? []).filter((m) => m.role === 'client'),
    [me?.memberships]
  );
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (m) =>
        (m.organization_name ?? '').toLowerCase().includes(q) ||
        (m.organization_slug ?? '').toLowerCase().includes(q)
    );
  }, [rows, query]);

  if (!accessToken) {
    return (
      <div className="space-y-3 w-full min-w-0">
        <h1 className="text-lg font-semibold tracking-tight">Организации</h1>
        <p className="text-sm text-[var(--text-muted)]">Войдите, чтобы увидеть организации, где у вас есть доступ.</p>
        <Link className="text-sm text-[var(--accent)] underline-offset-2 hover:underline" to={`${PATH.signIn}${suffix}`}>
          Войти
        </Link>
      </div>
    );
  }

  if (brandEmbedAllowed === true) {
    return <Navigate to={`${PATH.home}${suffix}`} replace />;
  }

  if (meLoading) {
    return (
      <div className="space-y-3 w-full min-w-0">
        <div className="h-5 w-40 animate-pulse rounded bg-[var(--bg-soft)]" />
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-14 w-full animate-pulse rounded-xl bg-[var(--bg-soft)]" />
          ))}
        </div>
      </div>
    );
  }

  if (meError) {
    return (
      <div className="space-y-3 w-full min-w-0">
        <p className="text-sm text-[var(--error-text)]">{meError}</p>
        <button
          type="button"
          className="text-sm text-[var(--accent)] underline-offset-2 hover:underline"
          onClick={() => reloadMe()}
        >
          Повторить
        </button>
      </div>
    );
  }

  async function handleSelect(targetOrgId: number, targetSlug: string | null | undefined) {
    setSwitchError(null);
    const slug = (targetSlug ?? '').trim();
    if (!slug) {
      setSwitchError('У этой организации не задан slug — откройте виджет по ссылке студии.');
      return;
    }
    if (!accessToken) return;
    setSwitchingOrgId(targetOrgId);
    try {
      const res = await postSelectClientOrganization(accessToken, { organization_id: targetOrgId });
      const rt = res.refresh_token ?? undefined;
      if (orgSlug) clearEmbedSession(orgSlug);
      setEmbedSession(slug, {
        accessToken: res.access_token,
        organizationId: targetOrgId,
        embedMode: 'client',
        refreshToken: rt
      });
      if (embedGlobal) {
        // Глобальный пикер: после выбора — на главную выбранной студии
        const nextSearch = searchWithOrgSlug(searchParams, slug);
        navigate({ pathname: PATH.home, search: nextSearch });
      } else {
        navigate(
          { pathname: location.pathname, search: searchWithOrgSlug(searchParams, slug) },
          { replace: true }
        );
      }
    } catch (e) {
      if (e instanceof ApiError) {
        setSwitchError(e.detail);
      } else {
        setSwitchError(e instanceof Error ? e.message : 'Не удалось переключить организацию');
      }
    } finally {
      setSwitchingOrgId(null);
    }
  }

  const isPickerMode = embedGlobal;

  return (
    <div className="space-y-4 w-full min-w-0">
      <div>
        <h1 className="text-lg font-semibold tracking-tight">
          {isPickerMode ? 'Выберите студию' : 'Мои организации'}
        </h1>
        {isPickerMode ? (
          <p className="mt-1 text-sm text-[var(--text-muted)]">
            Выберите студию для просмотра расписания и записи.
          </p>
        ) : null}
      </div>

      {rows.length > 3 ? (
        <div>
          <input
            type="search"
            placeholder="Поиск по названию…"
            className="min-h-10 w-full rounded-sm border-0 bg-[var(--bg-soft)] px-3 py-2 text-base text-[var(--text)] focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] sm:min-h-8 sm:text-[13px]"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      ) : null}

      {switchError ? (
        <p className="text-sm text-[var(--error-text)]" role="alert">{switchError}</p>
      ) : null}

      {filtered.length === 0 && rows.length === 0 ? (
        <p className="text-sm text-[var(--text-muted)]">
          Нет членств как клиент. Откройте виджет нужной студии для входа или регистрации.
        </p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-[var(--text-muted)]">Не найдено.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-[var(--border)] rounded-xl border border-[var(--border)]">
          {filtered.map((m) => {
            const active = m.organization_id === me?.organization_id;
            const busy = switchingOrgId === m.organization_id;
            const hasSlug = !!(m.organization_slug ?? '').trim();
            return (
              <li
                key={m.organization_id}
                className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-[var(--text)]">
                    {m.organization_name?.trim() || `Организация #${m.organization_id}`}
                  </p>
                  {!isPickerMode && m.organization_slug ? (
                    <p className="text-xs text-[var(--text-muted)]">{m.organization_slug}</p>
                  ) : null}
                </div>
                <div className="shrink-0">
                  {active && !isPickerMode ? (
                    <span className="text-xs font-medium text-[var(--accent)]">текущая</span>
                  ) : hasSlug ? (
                    <Button
                      type="button"
                      variant={isPickerMode ? 'primary' : 'secondary'}
                      className="w-full sm:w-auto"
                      disabled={busy || switchingOrgId != null}
                      onClick={() => void handleSelect(m.organization_id, m.organization_slug)}
                    >
                      {busy ? 'Переходим…' : isPickerMode ? 'Выбрать' : 'Сделать текущей'}
                    </Button>
                  ) : (
                    <span className="text-xs text-[var(--text-muted)]">откройте по ссылке студии</span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {!isPickerMode ? (
        <p className="text-sm">
          <Link className="text-[var(--accent)] underline-offset-2 hover:underline" to={`${PATH.home}${suffix}`}>
            На главную
          </Link>
        </p>
      ) : null}
    </div>
  );
}
